import React, { createContext, useEffect, useReducer } from 'react'
import axios from 'axios'
import { MatxLoading } from 'app/components'
import { API_URI } from 'config'
import { useNavigate } from 'react-router-dom'
import {
    AUTH_USE_HTTPONLY_COOKIE,
    clearAuthStorage,
    clearImpersonatorId,
    getAccessToken,
    getImpersonatorId,
    getRefreshToken,
    setImpersonatorId,
    setRole,
    setSuperAdminMode,
} from 'app/auth/authStorage'
import {
    applyAccessTokenToAxios,
    isAccessTokenExpired,
    persistAuthTokens,
    refreshAccessToken,
} from 'app/auth/authRefresh'
import { jwtDecode } from 'jwt-decode'

const initialState = {
    isAuthenticated: false,
    isInitialised: false,
    user: null,
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'INIT': {
            const { isAuthenticated, user } = action.payload

            return {
                ...state,
                isAuthenticated,
                isInitialised: true,
                user,
            }
        }
        case 'LOGIN': {
            const { user } = action.payload

            return {
                ...state,
                isAuthenticated: true,
                isInitialised: true,
                user,
            }
        }
        case 'UPDATE_USER': {
            const { user } = action.payload

            return {
                ...state,
                user,
            }
        }
        case 'LOGOUT': {
            return {
                ...state,
                isAuthenticated: false,
                user: null,
            }
        }
        case 'REGISTER': {
            const { user } = action.payload

            return {
                ...state,
                isAuthenticated: true,
                isInitialised: true,
                user,
            }
        }
        default: {
            return { ...state }
        }
    }
}

const AuthContext = createContext({
    ...initialState,
    method: 'JWT',
    login: () => Promise.resolve(),
    logout: () => {},
    register: () => Promise.resolve(),
    loginAsClient: () => Promise.resolve(),
    logBackToSuperAdmin: () => Promise.resolve(),
})

function applyLoginResponse(response) {
    const { token, accessToken, refreshToken, user } = response?.data || {}
    persistAuthTokens({
        accessToken: accessToken || token,
        refreshToken,
    })
    if (user?.role) {
        setRole(user.role)
    }
    return user
}

export function getApiErrorMessage(
    err,
    fallback = 'Request failed. Please try again.'
) {
    return (
        err?.response?.data?.error ||
        err?.response?.data?.msg ||
        err?.message ||
        fallback
    )
}

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const navigate = useNavigate()

    const login = async (email, mfaCode) => {
        const response = await axios.post(`${API_URI}/api/admin/auth/verify-otp`, {
            email,
            mfaCode,
        }, { withCredentials: true })

        const user = applyLoginResponse(response)
        dispatch({
            type: 'LOGIN',
            payload: { user },
        })

        try {
            const { data: fullUserData } = await axios.get(`${API_URI}/api/admin/me`)
            dispatch({
                type: 'UPDATE_USER',
                payload: { user: fullUserData },
            })
        } catch (err) {
            throw new Error(
                getApiErrorMessage(err, 'Failed to load user profile')
            )
        }
    }

    const loginAsClient = async (id) => {
        const currentToken = getAccessToken()
        if (currentToken) {
            try {
                const { _id, role, impersonatedBy } = jwtDecode(currentToken)
                if (role === 'SA' && _id) {
                    setImpersonatorId(_id)
                } else if (impersonatedBy) {
                    setImpersonatorId(impersonatedBy)
                }
            } catch {
                /* ignore malformed token */
            }
        }

        const response = await axios.post(
            `${API_URI}/api/admin/auth/login-as-client/${id}`,
            {},
            { withCredentials: true }
        )

        const user = applyLoginResponse(response)
        setSuperAdminMode(true)

        dispatch({
            type: 'LOGIN',
            payload: { user },
        })

        try {
            const { data: fullUserData } = await axios.get(`${API_URI}/api/admin/me`)
            
            dispatch({
                type: 'UPDATE_USER',
                payload: { user: fullUserData },
            })
        } catch (err) {
            console.warn('Failed to fetch full user profile:', err)
        }

        return true
    }

    const logBackToSuperAdmin = async () => {
        const impersonatedBy = getImpersonatorId()
        if (!impersonatedBy) {
            throw new Error('No super admin session to restore.')
        }

        let accessToken = getAccessToken()
        const hasRefresh =
            AUTH_USE_HTTPONLY_COOKIE || Boolean(getRefreshToken())
        if (
            (!accessToken || isAccessTokenExpired(accessToken)) &&
            hasRefresh
        ) {
            accessToken = await refreshAccessToken()
        }

        try {
            const response = await axios.post(
                `${API_URI}/api/admin/auth/login-as-sa`,
                { impersonatedBy },
                { withCredentials: true }
            )
            const user = applyLoginResponse(response)
            setSuperAdminMode(false)
            clearImpersonatorId()

            dispatch({ type: 'LOGIN', payload: { user } })
            try {
                const { data: fullUserData } = await axios.get(
                    `${API_URI}/api/admin/me`
                )
                dispatch({
                    type: 'UPDATE_USER',
                    payload: { user: fullUserData },
                })
                navigate('/dashboard/analytics')
            } catch (err) {
                console.warn('Failed to fetch full user profile:', err)
                navigate('/dashboard/analytics')
            }
            return true
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                'Failed to return to Super Admin'
            throw new Error(message)
        }
    }

    const register = async (email, password) => {
        const response = await axios.post(`${API_URI}/api/admin/auth/register`, {
            email,
            password,
        }, { withCredentials: true })

        const user = applyLoginResponse(response)

        dispatch({
            type: 'REGISTER',
            payload: { user },
        })
    }

    const logout = async () => {
        try {
            await axios.post(
                `${API_URI}/api/admin/auth/logout`,
                {},
                { withCredentials: true }
            )
        } catch {
            /* revoke locally even if API is unreachable */
        }
        clearAuthStorage()
        applyAccessTokenToAxios(null)
        dispatch({ type: 'LOGOUT' })
    }

    const initAuth = async (isMounted, abortController) => {
        try {
            let accessToken = getAccessToken()
            const hasRefresh =
                AUTH_USE_HTTPONLY_COOKIE || Boolean(getRefreshToken())

            if (hasRefresh && (!accessToken || isAccessTokenExpired(accessToken))) {
                try {
                    accessToken = await refreshAccessToken()
                } catch {
                    accessToken = null
                }
            } else if (accessToken) {
                applyAccessTokenToAxios(accessToken)
            }

            if (accessToken) {
                const { data } = await axios.get(`${API_URI}/api/admin/me`, {
                    signal: abortController.signal,
                })
                if (!isMounted) return

                dispatch({
                    type: 'INIT',
                    payload: {
                        isAuthenticated: true,
                        user: { ...data },
                    },
                })
            } else {
                if (!isMounted) return

                dispatch({
                    type: 'INIT',
                    payload: {
                        isAuthenticated: false,
                        user: null,
                    },
                })
                clearAuthStorage()
                navigate('/')
            }
        } catch (err) {
            if (err.name === 'AbortError' || err.name === 'CanceledError') return
            if (!isMounted) return

            dispatch({
                type: 'INIT',
                payload: {
                    isAuthenticated: false,
                    user: null,
                },
            })
            clearAuthStorage()
            applyAccessTokenToAxios(null)
            navigate('/')
        }
    }

    useEffect(() => {
        let isMounted = true
        const abortController = new AbortController()
        initAuth(isMounted, abortController)
        return () => {
            isMounted = false
            abortController.abort()
        }
    }, [])

    if (!state.isInitialised) {
        return <MatxLoading />
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'JWT',
                login,
                logout,
                register,
                loginAsClient,
                logBackToSuperAdmin,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export default AuthContext
